/* eslint-disable @typescript-eslint/no-explicit-any -- can't escape any*/
/* eslint-disable @typescript-eslint/no-unsafe-argument -- can't escape any */
import get from 'lodash/get';
import { safeJsonStringify } from '../../utils/json';
import { EXPRESSION_REGEX, STRINGIFIED_EXPRESSION } from '../../utils/regex';
import { compileGet } from './compileGet';
// oxlint-disable-next-line no-cycle -- pre-existing cycle (evaluateExpression imports defaultUpdater);
// isServerHardenedEval is only read at call-time inside the updaters, never at module init.
import { isServerHardenedEval } from './evaluateExpression';
import { getComputedData } from './getComputedData';
import { createExpressionEvaluator } from './indirectEval';

const NEWLINE_REGEX = /\r?\n|\r/g;
const WHITESPACE_REGEX = /\s+/g;

function convertToSingleLine(obj: object): string {
  return (safeJsonStringify(obj) as unknown as string)
    .replace(NEWLINE_REGEX, '') // Remove newlines
    .replace(WHITESPACE_REGEX, ' '); // Replace multiple spaces with single space
}

/**
 * Recursively updates the value of a given object with an updater function.
 */

export type Obj = Record<string, any> | undefined | null | string;
export function recursiveUpdate(obj: Obj, updater: (value: string | null | undefined) => Obj): Obj {
  // If obj is an object and not null
  if (typeof obj === 'object' && obj !== null) {
    // If obj is an array
    if (Array.isArray(obj)) {
      const result: Obj[] = [];
      for (const item of obj) {
        result.push(recursiveUpdate(item, updater));
      }
      return result;
    }

    /**
     * Date object does not have any keys to iterate over, it stores date internally
     * hence we need to return the date object as is. Otherwise in case of date object, this will return an empty object
     */
    if (obj instanceof Date) {
      return obj;
    }
    // If obj is a regular object
    const updatedObj: Obj = {};
    // Iterate over each key in obj
    for (const key in obj) {
      // Check if obj has the key (not inherited from prototype chain)
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        // Update the value at the key using recursiveUpdate
        updatedObj[key] = recursiveUpdate(obj[key], updater);
      }
    }
    return updatedObj;
  }

  // If obj is not an object, apply the updater function and return the result
  return updater(obj);
}

/**
 * Recursively call the updater function, if the key matches the with objectKey or if true is returned from value sector
 * @returns updated object
 */
export function recursiveUpdateWithSelector({
  obj,
  updater,
  valueSelector,
}: {
  obj: Obj | undefined | null;
  updater: (key: string, value: any) => unknown;
  valueSelector: (key: string, value: unknown) => boolean;
}): Obj {
  // If obj is an object and not null
  if (typeof obj === 'object' && obj !== null) {
    // If obj is an array
    if (Array.isArray(obj)) {
      // Map over each element in the array and apply recursiveUpdate
      return obj.map((value) =>
        recursiveUpdateWithSelector({
          obj: value as Obj,
          updater,
          valueSelector,
        }),
      );
    }

    /**
     * Date object does not have any keys to iterate over, it stores date internally
     * hence we need to return the date object as is. Otherwise in case of date object, this will return an empty object
     */
    if (obj instanceof Date) {
      return obj;
    }

    // If obj is a regular object
    const updatedObj: Obj = {};
    // Iterate over each key in obj
    for (const key in obj) {
      // Check if obj has the key (not inherited from prototype chain)
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const shouldUpdate = valueSelector(key, obj[key]);
        updatedObj[key] = shouldUpdate
          ? updater(key, obj[key])
          : recursiveUpdateWithSelector({
              obj: obj[key] as Obj,
              updater,
              valueSelector,
            });
      }
    }
    return updatedObj;
  }

  return obj as Obj;
}

/**
 * Resolves dynamic expressions within a value using a provided data context.
 *
 * - If the value is a string and contains one or more dynamic expressions (e.g., `{{user.name}}`), it replaces each expression with the corresponding value from `dataToResolveWith` using lodash's `get`.
 * - If the entire string is a single expression, it returns the resolved value directly (not as a string).
 * - If the resolved value is an object, it recursively computes its values using `getComputedData` and flattens the result to a single line.
 * - If the value is not a string or does not contain expressions, it returns the value as-is.
 *
 * @param dataToResolveWith - The object used as the context for resolving expressions.
 * @param valueToResolve - The value (string, object, etc.) that may contain dynamic expressions to resolve.
 * @param shouldEvaluateResult - A predicate function to determine if a result should be further evaluated.
 * @returns The resolved value, with all dynamic expressions replaced by their computed values.
 */
/**
 * Resolve one expression variable (e.g. "user.name" or "false") using a cached evaluator when
 * context is an object, otherwise compileGet. Avoids creating a new Function per variable.
 */
function resolveVariable(
  variable: string,
  dataToResolveWith: Obj,
  evaluator: ((script: string) => unknown) | null,
  // UA-004: when the server nested-eval flag is on (`harden`), a value the grammar captured
  // as a property path is READ by walking the object, never compiled. A "path" that smuggled
  // JS (e.g. `pageVariables['x'+<js>+'y']`) then resolves to a miss instead of executing.
  // The compiling evaluator / compileGet routes stay for the default, flag-off behaviour.
  harden = false,
): unknown {
  if (harden) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- lodash get on the resolve context
    return get(dataToResolveWith, variable);
  }
  if (evaluator !== null) {
    try {
      return evaluator(variable);
    } catch {
      // fallback when eval throws (e.g. missing path in strict mode)
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- compiled getter return
  return compileGet(variable)(dataToResolveWith);
}

// UA-003: when hardening, a resolved inner-binding value is spliced into the outer source as a
// JSON literal, not raw text — so `rows[{{key}}]` with an attack in `key` compiles to
// `rows["<attack as string>"]` (inert), while the legitimate deferred-index case `rows[2]` is
// unchanged. Any non-serialisable value collapses to `undefined` and fails closed.
function toSplicedLiteral(value: unknown): string {
  // JSON.stringify (not safeJsonStringify): we need a real JS literal in the generated
  // source. Functions / undefined stringify to `undefined`, which is inert.
  return JSON.stringify(value) ?? 'undefined';
}

// UA-004: a whole-string binding like `{{ pageVariables['x'] }}` is a pure property path.
// `stringToJS` strips the `{{ }}` marker, so by the time it reaches the compiler the path is
// indistinguishable from author JS and gets compiled — which is the injection. Under harden we
// short-circuit that: if the entire expression is one grammar-matched path, READ it with a
// non-compiling getter. Returns `{ handled: false }` for anything that isn't a pure path (real
// author expressions like `{{ a || b }}` don't full-match the grammar, so they fall through).
export function resolveHardenedPathExpression(
  expression: string,
  context: Obj,
): { handled: boolean; value?: unknown } {
  const match = expression.match(STRINGIFIED_EXPRESSION);
  const path = match?.[1];
  if (match?.[0] !== expression || !path) return { handled: false };
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- lodash get on the context
  return { handled: true, value: get(context, path) };
}

export const defaultUpdater = (
  dataToResolveWith: Obj,
  valueToResolve: any,
  shouldEvaluateResult: (result: string) => boolean,
) => {
  if (typeof valueToResolve === 'string') {
    if (valueToResolve.match(EXPRESSION_REGEX) === null) return valueToResolve;

    // isServerHardenedEval == the server nested-eval flag is on; our signal to close UA-003/UA-004
    // on this path (splice as literal, read paths without compiling). Flag off → unchanged behaviour.
    const harden = isServerHardenedEval(shouldEvaluateResult);

    const evaluator =
      typeof dataToResolveWith === 'object' && dataToResolveWith !== null
        ? createExpressionEvaluator(dataToResolveWith as Record<string, unknown>)
        : null;

    // when the entire string is an expression
    if (valueToResolve.match(STRINGIFIED_EXPRESSION)?.[0] === valueToResolve) {
      const strippedPath = valueToResolve.match(STRINGIFIED_EXPRESSION)?.[1];
      if (!strippedPath) return valueToResolve;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- value from evaluator or getter
      return resolveVariable(strippedPath, dataToResolveWith, evaluator, harden);
    }

    return valueToResolve.replaceAll(EXPRESSION_REGEX, (_match, variable: string) => {
      const value = resolveVariable(variable, dataToResolveWith, evaluator, harden);

      if (typeof value === 'object' && value !== null) {
        return convertToSingleLine(
          getComputedData(
            value as string | Record<string, unknown> | unknown[],
            dataToResolveWith as Record<string, unknown>,
            shouldEvaluateResult,
          ),
        );
      }
      // hardened: inner value becomes a JSON literal in the outer source, never raw text (UA-003)
      if (harden) return toSplicedLiteral(value);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- known return value either string | number | boolean | undefined | null
      return value as string;
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- same reason as top of the file
  return valueToResolve;
};

/**
 * Simplified bulk expression evaluator - reuses defaultUpdater logic
 * Returns tuple format for compatibility with bulk evaluation categorization
 *
 * Key simplification: removed complex pattern matching, just use same logic as defaultUpdater
 * but return { value, isHandledByGetter } for categorization in bulk evaluator
 */
export const newDefaultUpdater = (
  dataToResolveWith: Obj,
  valueToResolve: unknown,
  shouldEvaluateResult: (result: string) => boolean,
): { value: unknown; isHandledByGetter?: boolean } => {
  // fast path for non-strings
  if (typeof valueToResolve !== 'string') {
    return { value: valueToResolve };
  }

  const expressionMatches = valueToResolve.match(EXPRESSION_REGEX);
  // check if no expressions at all
  if (expressionMatches === null) {
    return { value: valueToResolve };
  }

  // see defaultUpdater: isServerHardenedEval == server nested-eval flag on == harden.
  const harden = isServerHardenedEval(shouldEvaluateResult);

  const evaluator =
    typeof dataToResolveWith === 'object' && dataToResolveWith !== null
      ? createExpressionEvaluator(dataToResolveWith as Record<string, unknown>)
      : null;

  const stringifiedMatch = valueToResolve.match(STRINGIFIED_EXPRESSION);
  // when the entire string is a single expression - return value directly
  if (stringifiedMatch?.[0] === valueToResolve) {
    const strippedPath = stringifiedMatch[1];
    if (!strippedPath) return { value: valueToResolve };

    const value = resolveVariable(strippedPath, dataToResolveWith, evaluator, harden);

    // if value is an object with potential nested expressions, recursively evaluate them
    if (typeof value === 'object' && value !== null) {
      const computedValue = getComputedData(
        value as Record<string, unknown>,
        dataToResolveWith as Record<string, unknown>,
        shouldEvaluateResult,
      );
      return { value: computedValue, isHandledByGetter: true };
    }

    return { value, isHandledByGetter: true };
  }

  // handle multiple expressions via string replacement (same as defaultUpdater)
  const result = valueToResolve.replaceAll(EXPRESSION_REGEX, (_match, variable: string) => {
    const value = resolveVariable(variable, dataToResolveWith, evaluator, harden);

    if (typeof value === 'object' && value !== null) {
      return convertToSingleLine(
        getComputedData(
          value as string | Record<string, unknown> | unknown[],
          dataToResolveWith as Record<string, unknown>,
          shouldEvaluateResult,
        ),
      );
    }
    // hardened: inner value becomes a JSON literal in the outer source, never raw text (UA-003)
    if (harden) return toSplicedLiteral(value);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return value as string;
  });

  return { value: result };
};
